import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabase/supabaseClient";

export interface User {
  id: string;
  email: string;
  nickname: string;
  userType: "fan" | "worker" | "artist" | "admin" | "special";
  level: "pending" | "full";
  evidence_url?: string | null;
  evidence_status?: string | null;
}

interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  logout: () => Promise<void>;
  loading: boolean; // ← 추가
}

const UserContext = createContext<UserContextType | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // ← 추가
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true); // 세션 체크 시작
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setUser(null);
        setLoading(false); // 세션 체크 완료
        return;
      }

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .eq("deleted", false)
        .single();

      if (!data || error) {
        alert("탈퇴된 계정이거나 정보가 없습니다.");
        setUser(null);
        setLoading(false); // 세션 체크 완료
        navigate("/login", { replace: true });
        return;
      }

      setUser({
        id: data.id,
        email: data.email,
        nickname: data.nickname || "",
        userType: data.user_type || "",
        level: data.level || "",
        evidence_url: data.evidence_url,
        evidence_status: data.evidence_status,
      });
      setLoading(false); // 세션 체크 완료
    };

    fetchProfile();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) fetchProfile();
      else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, [navigate]);

  async function logout() {
    await supabase.auth.signOut();
    setUser(null);
    setLoading(false);
  }

  return (
    <UserContext.Provider value={{ user, setUser, logout, loading }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("UserContext not found");
  return ctx;
}
